import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BarrelLayout,
	RollListItem,
	RollsetListItem,
} from "../models/RollshopOperations";

export interface GetRollsetsParams {
	signal?: AbortSignal;
}

export const getRollsetsList = async ({
	signal,
}: GetRollsetsParams = {}): Promise<RollsetListItem[]> => {
	const apiService = new MESApiService();
	// Pass nothing: every filter defaults to NULL and the procedure returns the
	// whole list, which is what this grid shows.
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsets]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollsetListItem[];
	}
	throw new Error(resp.message || "Error fetching rollsets");
};

export interface GetRollsParams {
	// Receive Date range filter (YYYY-MM-DD strings), passed as
	// @receiveDateFrom / @receiveDateTo. They trim the result set to rolls whose
	// ReceiveTimestamp falls in [from, to+1day).
	receiveDateFrom?: string | null;
	receiveDateTo?: string | null;
	signal?: AbortSignal;
}

export interface MillSizeOption {
	Id: number;
	Name: string;
	Description?: string | null;
	Active?: boolean | null;
}

export interface BarrelOption {
	Id: number;
	Name: string;
	StandId: number;
}

export const getMillSizes = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<MillSizeOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetMillSizes]",
		[{ name: "OnlyActives", value: true }],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as MillSizeOption[];
	}
	throw new Error(resp.message || "Error fetching mill sizes");
};

export const getBarrels = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<BarrelOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[ROLLS].[GetBarrels]", [], signal);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as BarrelOption[];
	}
	throw new Error(resp.message || "Error fetching barrels");
};

// Fetch the layouts (passes) of a single barrel — used by the Rollset
// detail panel to show the barrel-layout grid that the legacy IHBox has at
// the bottom of its rollset detail. Calls [ROLLS].[GetBarrels] with
// @Id + @IncludeLayout=1; layouts arrive in tables[1].
export const getBarrelLayouts = async ({
	barrelId,
	signal,
}: {
	barrelId: number;
	signal?: AbortSignal;
}): Promise<BarrelLayout[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: barrelId },
		{ name: "IncludeLayout", value: true },
	];
	const resp = await apiService.callV2(
		"[ROLLS].[GetBarrels]",
		parameters,
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[1].rows", []) as BarrelLayout[];
	}
	throw new Error(resp.message || "Error fetching barrel layouts");
};

export const getRollsList = async ({
	receiveDateFrom = null,
	receiveDateTo = null,
	signal,
}: GetRollsParams = {}): Promise<RollListItem[]> => {
	const apiService = new MESApiService();
	// ROLLS.GetRolls has no timezone parameter: it returns the raw columns and
	// the grid formats them. The proxy took one because the legacy procedure did.
	const parameters: spParameter[] = [
		{ name: "receiveDateFrom", value: receiveDateFrom },
		{ name: "receiveDateTo", value: receiveDateTo },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRolls]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollListItem[];
	}
	throw new Error(resp.message || "Error fetching rolls");
};

// Write call — always targets the new ROLLS schema (no flag, no legacy
// branch). Project rule (feedback_no_writes_to_legacy): never mutate the
// legacy DB from the new system. We call the SP directly — no proxy wrapper
// needed because the SP has a clean `@User` parameter and no signature
// translation is required.
export const deleteRoll = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: id }];

	const resp = await apiService.callV2("[ROLLS].[DeleteRoll]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting roll");
	}
};

export interface RollUpsertPayload {
	Id: number | null;
	Name: string | null;
	Description: string | null;
	RollApplicationId: number | null;
	BlankDiameter: number | null;
	Diameter: number | null;
	Hardness: number | null;
	RollMaterialId: number | null;
	MillSizeId: number | null;
	Cost: number | null;
	CostCenterId: number | null;
	OrderTimestamp: string | null;
	ReceiveTimestamp: string | null;
	InvoiceTimestamp: string | null;
	PurchaseOrderItemId: number | null;
	SupplierId: number | null;
	SupplierInvoiceNumber: string | null;
	Returned: string | null;
	/** Sent only when present: null then clears it, unlike the other fields. */
	ScrapWeight?: number | null;
}

export const upsertRoll = async (
	roll: RollUpsertPayload,
): Promise<{ Id: number }> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: roll.Id },
		{ name: "Name", value: roll.Name },
		{ name: "Description", value: roll.Description },
		{ name: "RollApplicationId", value: roll.RollApplicationId },
		{ name: "BlankDiameter", value: roll.BlankDiameter },
		{ name: "Diameter", value: roll.Diameter },
		{ name: "Hardness", value: roll.Hardness },
		{ name: "RollMaterialId", value: roll.RollMaterialId },
		{ name: "MillSizeId", value: roll.MillSizeId },
		{ name: "Cost", value: roll.Cost },
		{ name: "CostCenterId", value: roll.CostCenterId },
		{ name: "OrderTimestamp", value: roll.OrderTimestamp },
		{ name: "ReceiveTimestamp", value: roll.ReceiveTimestamp },
		{ name: "InvoiceTimestamp", value: roll.InvoiceTimestamp },
		{ name: "PurchaseOrderItemId", value: roll.PurchaseOrderItemId },
		{ name: "SupplierId", value: roll.SupplierId },
		{ name: "SupplierInvoiceNumber", value: roll.SupplierInvoiceNumber },
		{ name: "Returned", value: roll.Returned },
		...(roll.ScrapWeight !== undefined
			? [
					{ name: "ScrapWeight", value: roll.ScrapWeight },
					{ name: "SetScrapWeight", value: true },
				]
			: []),
	];

	const resp = await apiService.callV2("[ROLLS].[UpsertRoll]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", { Id: 0 }) as { Id: number };
	}
	throw new Error(resp.message || "Error saving roll");
};

// =============================================================================
// Auxiliary catalogs for the Rolls form (suppliers, cost centers, applications,
// materials). All catalog data is the same in legacy and new (synced via
// RBD.SyncAllCatalogsJob), so we read directly from the new schema without
// proxy bifurcation.
// =============================================================================

export interface CatalogOption {
	Id: number;
	Name: string;
	Active?: boolean | null;
}

export const getRollApplications = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollApplications]",
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	}
	throw new Error(resp.message || "Error fetching roll applications");
};

export const getRollMaterials = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollMaterials]",
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	}
	throw new Error(resp.message || "Error fetching roll materials");
};

export const getCostCenters = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetCostCenters]",
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	}
	throw new Error(resp.message || "Error fetching cost centers");
};

export const getSuppliers = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetSuppliers]",
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	}
	throw new Error(resp.message || "Error fetching suppliers");
};

// =============================================================================
// Rollsets — write proxies + auxiliary catalogs
// =============================================================================

export interface RollsetUpsertPayload {
	Id: number | null;
	Name: string | null;
	StandId: number | null;
	MillSizeId: number | null;
	BarrelId: number | null;
	StatusCode: string | null;
	Comments: string | null;
}

export const upsertRollset = async (
	rs: RollsetUpsertPayload,
): Promise<{ Id: number }> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: rs.Id },
		{ name: "Name", value: rs.Name },
		{ name: "StatusCode", value: rs.StatusCode },
		{ name: "StandId", value: rs.StandId },
		{ name: "MillSizeId", value: rs.MillSizeId },
		{ name: "BarrelId", value: rs.BarrelId },
		{ name: "Comments", value: rs.Comments },
	];

	const resp = await apiService.callV2("[ROLLS].[UpsertRollset]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", { Id: 0 }) as { Id: number };
	}
	throw new Error(resp.message || "Error saving rollset");
};

export interface AssignRollPayload {
	rollsetId: number;
	rollId: number;
	rollPosition: string; // 'TOP' | 'BOTTOM' | '1' | '2' | '3' | '4' | '5'
}

export const assignRollToRollset = async (
	p: AssignRollPayload,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "rollsetId", value: p.rollsetId },
		{ name: "rollId", value: p.rollId },
		{ name: "rollPosition", value: p.rollPosition },
	];
	const resp = await apiService.callV2(
		"[ROLLS].[AssignRollToRollset]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error assigning roll");
	}
};

export interface RemoveRollPayload {
	rollsetId: number;
	rollPosition: string;
	newStatusCode: string; // 'FREE' | 'SCRAPPED'
	changeReasonId: number | null;
	comments: string | null;
}

export const removeRollFromRollset = async (
	p: RemoveRollPayload,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "rollsetId", value: p.rollsetId },
		{ name: "newStatusCode", value: p.newStatusCode },
		{ name: "changeReasonId", value: p.changeReasonId },
		{ name: "comments", value: p.comments },
		{ name: "rollPosition", value: p.rollPosition },
	];
	const resp = await apiService.callV2(
		"[ROLLS].[RemoveRollFromRollset]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error removing roll");
	}
};

export const setRollsetStatus = async (
	id: number,
	statusCode: string,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "id", value: id },
		{ name: "statusCode", value: statusCode },
	];
	const resp = await apiService.callV2(
		"[ROLLS].[SetRollsetStatus]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error setting rollset status");
	}
};

// WARNING: the new barrel has fewer passes and the rollset has production, so some records are lost.
export type BarrelChangeValidation = "SUCCESS" | "WARNING" | "ERROR";

export const validateRollsetBarrelChange = async (
	rollsetId: number,
	barrelId: number,
): Promise<BarrelChangeValidation> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "rollsetId", value: rollsetId },
		{ name: "barrelId", value: barrelId },
	];
	const resp = await apiService.callV2(
		"[ROLLS].[ValidateRollsetBarrelChange]",
		parameters,
	);
	if (resp.ok) {
		return get(
			resp,
			"data.tables[0].rows[0].Result",
			"ERROR",
		) as BarrelChangeValidation;
	}
	throw new Error(resp.message || "Error validating the barrel change");
};

// Only the barrel goes: the rest of the rollset keeps its values. Legacy always merges once validated.
export const changeRollsetBarrel = async (
	rollsetId: number,
	barrelId: number,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: rollsetId },
		{ name: "BarrelId", value: barrelId },
		{ name: "MergeProduction", value: true },
	];
	const resp = await apiService.callV2("[ROLLS].[UpsertRollset]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error changing the barrel");
	}
};

export const removeBarrelFromRollset = async (
	rollsetId: number,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "id", value: rollsetId }];
	const resp = await apiService.callV2(
		"[ROLLS].[RemoveBarrelFromRollset]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error removing the barrel");
	}
};

export const removeRollset = async (rollsetId: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: rollsetId }];
	const resp = await apiService.callV2("[ROLLS].[RemoveRollset]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the rollset");
	}
};

export interface RollsetStatusOption {
	Code: string;
	Name: string;
	Description?: string | null;
}

export const getRollsetStatus = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<RollsetStatusOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsetStatus]",
		[],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollsetStatusOption[];
	}
	throw new Error(resp.message || "Error fetching rollset status");
};

export interface RollChangeReason {
	Id: number;
	Name: string;
	RollStatusCode: string | null;
	Description: string | null;
	Active: boolean | null;
}

export const getRollChangeReasons = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<RollChangeReason[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollChangeReasons]",
		[{ name: "OnlyActives", value: true }],
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollChangeReason[];
	}
	throw new Error(resp.message || "Error fetching roll change reasons");
};

// Used by the AssignRollToRollset modal: list rolls available to assign to a
// position. Legacy filter: status FREE or SCRAPPED + same MillSize as the
// rollset.
export const getRollsForAssign = async ({
	millSizeId,
	signal,
}: {
	millSizeId: number | null;
	signal?: AbortSignal;
}): Promise<RollListItem[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "millSizeId", value: millSizeId },
		// The native procedure accepts one status or a pipe-separated list.
		{ name: "statusCode", value: "FREE|SCRAPPED" },
	];
	const resp = await apiService.callV2(
		"[ROLLS].[GetRolls]",
		parameters,
		signal,
	);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollListItem[];
	}
	throw new Error(resp.message || "Error fetching rolls for assignment");
};
